import React, {useCallback} from 'react';
import {HStack, useDisclosure, useTheme} from '@chakra-ui/react';
import RequiresEarlyAccess from '../RequiresEarlyAccess';
import Icon from '../Icon';
import {TextG14} from '../typography';
import PickerModal from './PickerModal';

export type LocationMode = 'home' | 'gps' | 'custom';

export interface SelectedLocation {
  mode: LocationMode;
  // Resolved coords. Null only when mode === 'home' (the backend resolves home from the
  // user's saved address — sending coords would be treated as an override).
  latitude: number | null;
  longitude: number | null;
  label: string;
}

interface Props {
  selected: SelectedLocation;
  onChange: (next: SelectedLocation) => void;
}

export const DEFAULT_HOME_SELECTION: SelectedLocation = {
  mode: 'home',
  latitude: null,
  longitude: null,
  label: 'Home',
};

/**
 * v2 location picker for the Bookmark Ahead feed: pill (in the Hyper-Local section header)
 * + centered modal with segmented mode picker, address autocomplete, and a draggable map
 * marker. Replaces the v1 BookmarkAheadLocationSelector (kept in tree as a rollback).
 *
 * Self-gates on `<RequiresEarlyAccess>`. The modal commits via Apply only — pin drags and
 * mode flips are local until the user confirms, so a single feed refetch happens per
 * intended change rather than per drag tick.
 */
const BookmarkAheadLocationPicker: React.FC<Props> = ({selected, onChange}) => {
  const theme = useTheme();
  const {isOpen, onOpen, onClose} = useDisclosure();

  const handleApply = useCallback(
    (next: SelectedLocation) => {
      onChange(next);
    },
    [onChange],
  );

  // Truncate long addresses so the pill doesn't break the layout.
  const pillLabel =
    selected.label.length > 30 ? `${selected.label.slice(0, 28)}…` : selected.label;

  return (
    <RequiresEarlyAccess>
      <HStack
        as="button"
        onClick={onOpen}
        spacing={2}
        align="center"
        bg="white"
        border="1px solid #E5E5E5"
        borderRadius="100px"
        px={4}
        h="36px"
        maxW="100%"
        minW={0}>
        <TextG14 mt={0} lineHeight="1" color={theme.colors.grey} flexShrink={0}>
          Searching Near:
        </TextG14>
        <TextG14 mt={0} lineHeight="1" fontWeight="700" color="#262626" isTruncated>
          {pillLabel}
        </TextG14>
        <Icon
          iconName="fi fi-rr-angle-down"
          style={{
            fontSize: '10px',
            color: '#262626',
            display: 'block',
            lineHeight: 1,
            // Flaticon chevrons render in the upper half of their em-box, so the visual glyph
            // sits above the geometric center. 2px nudge fixes it.
            transform: 'translateY(2px)',
          }}
        />
      </HStack>

      <PickerModal
        isOpen={isOpen}
        onClose={onClose}
        selected={selected}
        onApply={handleApply}
      />
    </RequiresEarlyAccess>
  );
};

export default BookmarkAheadLocationPicker;
